import { useAuth } from '../auth/auth.store.js';
import { platform } from '../platform.js';
import { getApiBase } from './base.js';
/**
 * The one place any client application talks to the API.
 *
 * Two behaviours are centralised here because getting either wrong in one call
 * site would be invisible until the field found it — and now that there are
 * three applications, "invisible" would also mean "only in one of them":
 *
 * REFRESH ON 401, ONCE. The access token lives 15 minutes (TAD 4.6) and a
 * salesperson's session lasts a working day, so expiry mid-visit is the normal
 * case rather than the exception. A single retry after a successful refresh
 * keeps that invisible; retrying forever would turn a revoked session into an
 * infinite loop against the auth endpoint.
 *
 * CREDENTIALS COME FROM THE PLATFORM ADAPTER. On web they are always included:
 * the refresh token is an HttpOnly cookie the page cannot read, which is the
 * point — it means the browser has to be told to send it, on every call, or the
 * refresh path silently stops working. That survives the split to two origins
 * because the cookie is `SameSite=strict` and SameSite is evaluated per
 * registrable domain, not per origin, so both apps and the API stay under one
 * eTLD+1 (ADR-0005).
 *
 * The native client sits outside that model entirely — no browser, no cookie
 * jar worth trusting — and carries its refresh token in the platform keystore
 * instead. Hence `platform().credentials` rather than a constant: the two
 * transports are genuinely different and the difference belongs in one place.
 */
export class ApiError extends Error {
    status;
    body;
    constructor(status, message, body) {
        super(message);
        this.status = status;
        this.body = body;
    }
}
/**
 * The endpoints that PROVE A CREDENTIAL rather than merely consume a session.
 *
 * A 401 from these means the credentials were wrong. A 401 from anything else
 * means the access token expired. Conflating the two is how "invalid
 * credentials" reaches the user as "your session ended, please sign in again"
 * — on the sign-in screen, which is nonsense advice for a mistyped password.
 *
 * `/auth/password` IS ONE OF THEM, and leaving it out was the bug QC found:
 * changing your own password re-asks for the current one and the server answers
 * a wrong answer with a 401 (identity.service.ts, `changeOwnPassword`). Read as
 * an expiry, that 401 spent a refresh, retried the same wrong password, and then
 * signed the user out of a session that was never in question — so a typo threw
 * them onto the login screen claiming their session had ended, and the server's
 * actual "NIK atau kata sandi salah" was discarded unread. It differs from the
 * three below only in that it also NEEDS a live token; a genuinely expired one
 * costs the user one retry, which is the cheaper of the two failures by far.
 *
 * Matched exactly, not by prefix: `/auth/totp/enrol` is an authenticated
 * endpoint where a 401 genuinely does mean the token expired.
 */
const SESSION_ENDPOINTS = new Set(['/auth/login', '/auth/totp', '/auth/refresh', '/auth/password']);
function provesCredential(path) {
    return SESSION_ENDPOINTS.has(path.split('?')[0] ?? path);
}
/**
 * Thrown by whichever request happened to be in flight when the session died.
 *
 * Rarely read: signing out re-renders both applications straight to their login
 * screen, so the component that would have shown this is usually gone before it
 * can. It still has to be right for the mutation that reports through a toast.
 */
const SESSION_ENDED = 'sesi berakhir, silakan masuk kembali';
/**
 * NO RESPONSE ARRIVED — which is a different thing from a response that says no,
 * and used to be reported as the same one.
 *
 * Raised in two places, because there are two ways to get no answer. The
 * renewal could not be reached: a salesperson who walks into a stockroom
 * mid-visit gets this instead of being thrown out of a session that is still
 * perfectly valid. Or the REQUEST ITSELF could not be reached, which is the
 * commoner case by far and was not handled at all — see `NO_RESPONSE`.
 *
 * It reads as what it is, a connectivity problem, and the queued work retries on
 * its own.
 */
const SERVER_UNREACHABLE = 'tidak dapat menghubungi server, coba lagi saat jaringan kembali';
/**
 * The status an `ApiError` carries when there is no HTTP status to carry.
 *
 * ZERO RATHER THAN 503, and the difference is not pedantry. `error.status >= 500`
 * is a branch both `loginErrorMessage` and `totpErrorMessage` take, and it says
 * "Server sedang bermasalah" — the server has a problem. That is a claim about
 * something we never reached, and it is the wrong advice for the phone with no
 * signal that produces this most of the time. Zero matches no branch, so those
 * mappers fall through to `error.message` above, which describes the actual
 * situation. It is also what `XMLHttpRequest` has always reported for a request
 * that never got a reply.
 */
export const NO_RESPONSE = 0;
async function request(path, init = {}, retry = true) {
    const token = useAuth.getState().accessToken;
    // FormData must set its own multipart boundary; naming a content-type here
    // would produce a request the server cannot parse.
    const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;
    /*
     * THE FETCH THAT WAS NOT GUARDED, AND EVERY CONSUMER PAID FOR IT.
     *
     * `fetch` rejects — it does not resolve with a status — whenever no response
     * comes back: DNS failure, a dropped connection, a TLS error, a request
     * blocked by an extension, and, importantly, a 5xx from a load balancer in
     * front of an api that is restarting, because a gateway error page carries no
     * CORS headers and the browser refuses to hand it over. The manager console
     * sees the last of those as an intermittent "CORS error" that a refresh cures.
     *
     * Left unguarded that arrives at call sites as a bare `TypeError: Failed to
     * fetch`, so every `catch` narrowing on `instanceof ApiError` — the upload
     * queue's retry rule, the 409 handlers in the staff app — silently missed the
     * commonest failure a field client has. `refresh` was given this distinction
     * and the request path was not, which left the two halves of one client
     * disagreeing about what "unreachable" is.
     */
    let response;
    try {
        response = await fetch(`${getApiBase()}${path}`, {
            ...init,
            credentials: platform().credentials,
            headers: {
                ...(init.body && !isFormData ? { 'content-type': 'application/json' } : {}),
                ...(token ? { authorization: `Bearer ${token}` } : {}),
                ...init.headers,
            },
        });
    }
    catch (error) {
        // A cancellation is the CALLER'S OWN doing — a component unmounting, a
        // query superseded. Dressing it up as a connection failure would put a
        // network banner on screen for a request nobody was waiting for any more.
        if (error instanceof Error && error.name === 'AbortError')
            throw error;
        throw new ApiError(NO_RESPONSE, SERVER_UNREACHABLE);
    }
    /*
     * A 401 while holding a token means that token is expired or invalid — the
     * API refuses to say which (auth.guard.ts), and the client does not need to
     * know. Either way the session gets exactly one chance to renew itself, and
     * if it cannot, it ends here.
     *
     * The `token` check is what separates "the session died" from "there was no
     * session": without it, a 401 on a page nobody has signed into burned a
     * pointless refresh round-trip and then reported an expiry that never
     * happened.
     */
    if (response.status === 401 && token !== null && !provesCredential(path)) {
        if (retry) {
            const outcome = await useAuth.getState().refresh();
            if (outcome === 'REFRESHED')
                return request(path, init, false);
            /*
             * NOT REACHING THE SERVER IS NOT THE SERVER SAYING NO.
             *
             * This is the bug that logged field staff out of live sessions. The
             * renewal returned a bare `false` for a dropped connection, a 502 during
             * a deployment and a genuinely revoked family alike, and this line read
             * every one of them as "the session is over" — so a phone that lost
             * signal for the two seconds the refresh was in flight landed on the
             * login screen with a fourteen-day credential still in its keystore.
             *
             * The session is left exactly as it was and the caller gets a network
             * error. The upload queue already treats that as "retry later", which is
             * the correct behaviour and is now what happens.
             */
            if (outcome === 'UNAVAILABLE')
                throw new ApiError(NO_RESPONSE, SERVER_UNREACHABLE);
        }
        /*
         * Two ways to arrive here, and both mean the same thing to the user.
         *
         * The refresh was REFUSED — no cookie, expired cookie, or a revoked token
         * family. Or the refresh SUCCEEDED and the freshly-minted token was refused
         * anyway, which is what a deactivated account, a revoked session or a
         * server-side key rotation looks like from here. That second path used to
         * fall through to the generic error below, leaving the console signed in
         * with a token the API would never accept again: every screen an error
         * banner, and no way out but a manual reload.
         */
        useAuth.getState().signOut('EXPIRED');
        throw new ApiError(401, SESSION_ENDED);
    }
    if (!response.ok) {
        const body = await response.json().catch(() => undefined);
        const message = typeof body === 'object' && body !== null && 'message' in body
            ? String(body.message)
            : `permintaan gagal (${response.status})`;
        throw new ApiError(response.status, message, body);
    }
    if (response.status === 204)
        return undefined;
    // A 2xx whose body did not finish arriving. The same dropped connection as
    // above, caught a few milliseconds later, and it must read the same way
    // rather than as an unexplained `SyntaxError` from the JSON parser.
    try {
        return (await response.json());
    }
    catch {
        throw new ApiError(NO_RESPONSE, SERVER_UNREACHABLE);
    }
}
export const api = {
    get: (path) => request(path),
    post: (path, body) => request(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
    patch: (path, body) => request(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
    /**
     * Whole-resource replace. Used by the role/permission matrix, which genuinely
     * replaces a role's entire permission set rather than amending it.
     */
    put: (path, body) => request(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
    /**
     * The only DELETE in this API, and it will stay that way: master data is
     * deactivated because visits reference it. The exception is a SKU reference
     * photograph, which nothing historical points at — see the repository method
     * for the full argument.
     */
    delete: (path) => request(path, { method: 'DELETE' }),
    /**
     * Multipart upload through the API — currently only the master-data importer,
     * which reads a form field named exactly `file`. Distinct from
     * `uploadToStorage` below: this one is authenticated and goes to our own
     * server.
     */
    postForm: (path, form) => request(path, { method: 'POST', body: form }),
};
/*
 * `uploadToStorage` USED TO LIVE HERE and deliberately does not any more.
 *
 * It took a `Blob`, which is where the problem is: on web the photo genuinely
 * is one, but on React Native it is a file on disk, and handing a file-backed
 * blob to `fetch` marshals the bytes across the bridge — a documented source of
 * memory blowups and silent corruption on images this size. Native has to
 * stream from disk instead (`FileSystem.uploadAsync`), which is a different
 * function taking a different argument, not a polyfill of this one.
 *
 * So the two implementations live with their platforms: the web one in
 * `@fieldvision/web-core`, the native one in the upload queue's file driver.
 * Both keep the property that matters — no bearer token and no credentials go
 * to object storage (TAD 4.3 step 3) — and both must throw on a non-2xx, which
 * is easy to forget on native because `uploadAsync` returns the status rather
 * than rejecting.
 */
//# sourceMappingURL=client.js.map